/**
 * Canvas Integration Service
 * Enables AI agents to generate and display images, effects, and overlays on the canvas
 */

import { CanvasAction } from '../core/agent-controller';
import { ImageGenerationOptions, ImageResponse } from '../core/types';

export interface CanvasElement {
  id: string;
  type: 'image' | 'text' | 'effect' | 'overlay';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  style: CanvasElementStyle;
  duration?: number;
  animation?: CanvasAnimation;
  layer: number;
  visible: boolean;
  createdAt: Date;
}

export interface CanvasElementStyle {
  opacity: number;
  rotation: number;
  scale: number;
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  filter?: string;
  borderRadius?: number;
  shadow?: {
    x: number;
    y: number;
    blur: number;
    color: string;
  };
}

export interface CanvasAnimation {
  type: 'fade-in' | 'fade-out' | 'slide-in' | 'scale-in' | 'bounce' | 'pulse';
  duration: number;
  easing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';
  delay?: number;
  repeat?: boolean;
}

export interface ImagePromptTemplate {
  id: string;
  name: string;
  template: string;
  category: 'cute' | 'anime' | 'realistic' | 'artistic' | 'abstract';
  variables: string[];
  style: string;
}

export class CanvasIntegrationService {
  private elements = new Map<string, CanvasElement>();
  private nextElementId = 1;
  private nextLayer = 1;

  private promptTemplates: ImagePromptTemplate[] = [
    {
      id: 'cute-chibi',
      name: 'Cute Chibi Style',
      template:
        'adorable chibi {subject}, kawaii style, pastel colors, big eyes, {emotion} expression',
      category: 'cute',
      variables: ['subject', 'emotion'],
      style: 'chibi, kawaii, cute, pastel',
    },
    {
      id: 'anime-portrait',
      name: 'Anime Portrait',
      template: 'beautiful anime {subject}, {emotion} expression, detailed eyes, {art_style}',
      category: 'anime',
      variables: ['subject', 'emotion', 'art_style'],
      style: 'anime, detailed, high quality',
    },
    {
      id: 'conversation-scene',
      name: 'Conversation Scene',
      template: 'illustration of {scene_description}, {mood} atmosphere, digital art',
      category: 'artistic',
      variables: ['scene_description', 'mood'],
      style: 'digital art, illustration, detailed',
    },
    {
      id: 'emotion-abstract',
      name: 'Emotion Abstract',
      template: 'abstract representation of {emotion}, flowing shapes, {color_scheme} colors',
      category: 'abstract',
      variables: ['emotion', 'color_scheme'],
      style: 'abstract, artistic, emotional',
    },
  ];

  /**
   * Process a canvas action and return the necessary data for execution
   */
  async processCanvasAction(
    action: CanvasAction,
    imageProvider?: any,
    context?: Record<string, any>
  ): Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }> {
    try {
      switch (action.action) {
        case 'draw':
          return await this.handleDrawAction(action, imageProvider, context);

        case 'clear':
          return this.handleClearAction(action);

        case 'overlay':
          return this.handleOverlayAction(action);

        case 'background':
          return this.handleBackgroundAction(action);

        case 'effect':
          return this.handleEffectAction(action);

        default:
          return { success: false, error: `Unknown canvas action: ${action.action}` };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  private async handleDrawAction(
    action: CanvasAction,
    imageProvider?: any,
    context?: Record<string, any>
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    if (!imageProvider) {
      return { success: false, error: 'Image provider not available' };
    }

    try {
      // Generate image prompt from the action value
      const prompt = this.generateImagePrompt(action.value, context);

      // Generate image using the AI provider
      const imageOptions: ImageGenerationOptions = {
        model: 'dall-e-3',
        size: '1024x1024',
        quality: 'standard',
        style: 'vivid',
        n: 1,
      };

      const imageResponse: ImageResponse = await imageProvider.generateImage(prompt, imageOptions);

      if (!imageResponse.images || imageResponse.images.length === 0) {
        return { success: false, error: 'Failed to generate image' };
      }

      // Create canvas element
      const element = this.createElement({
        type: 'image',
        content: imageResponse.images[0].url || imageResponse.images[0].base64 || '',
        position: action.position || { x: 0.5, y: 0.5 },
        size: action.size || { width: 300, height: 300 },
        style: {
          opacity: 1,
          rotation: 0,
          scale: 1,
          borderRadius: 8,
        },
        duration: action.duration,
        animation: {
          type: 'fade-in',
          duration: 1000,
          easing: 'ease-out',
        },
      });

      return {
        success: true,
        data: {
          element,
          imageUrl: imageResponse.images[0].url,
          prompt: prompt,
          revisedPrompt: imageResponse.images[0].revisedPrompt,
        },
      };
    } catch (error: any) {
      return { success: false, error: `Image generation failed: ${error.message}` };
    }
  }

  private handleClearAction(action: CanvasAction): { success: boolean; data?: any } {
    if (action.value === 'all') {
      this.elements.clear();
    } else {
      // Clear specific layer or element
      const layer = parseInt(action.value);
      if (!isNaN(layer)) {
        for (const [id, element] of this.elements) {
          if (element.layer === layer) {
            this.elements.delete(id);
          }
        }
      } else {
        // Clear by ID
        this.elements.delete(action.value);
      }
    }

    return {
      success: true,
      data: { cleared: action.value },
    };
  }

  private handleOverlayAction(action: CanvasAction): { success: boolean; data?: any } {
    const element = this.createElement({
      type: 'overlay',
      content: action.value,
      position: action.position || { x: 0, y: 0 },
      size: action.size || { width: 1, height: 1 }, // Full screen
      style: {
        opacity: 0.8,
        rotation: 0,
        scale: 1,
        color: 'rgba(0, 0, 0, 0.3)',
      },
      duration: action.duration,
    });

    return {
      success: true,
      data: { element },
    };
  }

  private handleBackgroundAction(action: CanvasAction): { success: boolean; data?: any } {
    const element = this.createElement({
      type: 'image',
      content: action.value, // URL or color
      position: { x: 0, y: 0 },
      size: { width: 1, height: 1 }, // Full screen
      style: {
        opacity: 1,
        rotation: 0,
        scale: 1,
      },
      layer: -1, // Background layer
      duration: action.duration,
    });

    return {
      success: true,
      data: { element },
    };
  }

  private handleEffectAction(action: CanvasAction): { success: boolean; data?: any } {
    const effects = {
      sparkles: this.createSparkleEffect(action),
      hearts: this.createHeartsEffect(action),
      confetti: this.createConfettiEffect(action),
      glow: this.createGlowEffect(action),
      rain: this.createRainEffect(action),
    };

    const effect = effects[action.value as keyof typeof effects];
    if (!effect) {
      return { success: false, data: { error: `Unknown effect: ${action.value}` } };
    }

    return {
      success: true,
      data: { effect },
    };
  }

  private generateImagePrompt(userPrompt: string, context?: Record<string, any>): string {
    // Try to match with a template
    const template = this.findBestTemplate(userPrompt);

    if (template && context) {
      let prompt = template.template;

      // Replace variables with context values
      template.variables.forEach((variable) => {
        const value = context[variable] || this.getDefaultValue(variable, context);
        prompt = prompt.replace(`{${variable}}`, value);
      });

      // Add style guidelines
      prompt += `, ${template.style}, high quality, detailed`;

      return prompt;
    }

    // Fallback to user prompt with enhancements
    const enhancedPrompt = this.enhancePrompt(userPrompt, context);
    return `${enhancedPrompt}, digital art, high quality, detailed`;
  }

  private findBestTemplate(userPrompt: string): ImagePromptTemplate | null {
    const lowerPrompt = userPrompt.toLowerCase();

    for (const template of this.promptTemplates) {
      const keywords = template.name.toLowerCase().split(' ');
      if (keywords.some((keyword) => lowerPrompt.includes(keyword))) {
        return template;
      }
    }

    // Check by category keywords
    if (lowerPrompt.includes('cute') || lowerPrompt.includes('kawaii')) {
      return this.promptTemplates.find((t) => t.category === 'cute') || null;
    }
    if (lowerPrompt.includes('anime')) {
      return this.promptTemplates.find((t) => t.category === 'anime') || null;
    }
    if (lowerPrompt.includes('abstract')) {
      return this.promptTemplates.find((t) => t.category === 'abstract') || null;
    }

    return null;
  }

  private enhancePrompt(prompt: string, context?: Record<string, any>): string {
    let enhanced = prompt;

    // Add context-based enhancements
    if (context?.emotion) {
      enhanced += `, ${context.emotion} mood`;
    }

    if (context?.conversationTone) {
      enhanced += `, ${context.conversationTone} atmosphere`;
    }

    // Add quality modifiers
    enhanced += ', beautiful, aesthetic, trending on artstation';

    return enhanced;
  }

  private getDefaultValue(variable: string, context?: Record<string, any>): string {
    const defaults: Record<string, string> = {
      subject: context?.agentName || 'character',
      emotion: context?.emotion || 'happy',
      art_style: 'detailed anime style',
      scene_description: 'conversation between friends',
      mood: context?.mood || 'cheerful',
      color_scheme: 'warm',
    };

    return defaults[variable] || variable;
  }

  private createElement(params: {
    type: 'image' | 'text' | 'effect' | 'overlay';
    content: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    style: Partial<CanvasElementStyle>;
    duration?: number;
    animation?: CanvasAnimation;
    layer?: number;
  }): CanvasElement {
    const id = `element-${this.nextElementId++}`;

    const element: CanvasElement = {
      id,
      type: params.type,
      content: params.content,
      position: params.position,
      size: params.size,
      style: {
        opacity: 1,
        rotation: 0,
        scale: 1,
        ...params.style,
      },
      duration: params.duration,
      animation: params.animation,
      layer: params.layer ?? this.nextLayer++,
      visible: true,
      createdAt: new Date(),
    };

    this.elements.set(id, element);

    // Auto-remove after duration
    if (params.duration) {
      setTimeout(() => {
        this.elements.delete(id);
      }, params.duration);
    }

    return element;
  }

  // Effect creators
  private createSparkleEffect(action: CanvasAction) {
    return {
      type: 'particle',
      particles: 20,
      position: action.position || { x: 0.5, y: 0.5 },
      animation: 'sparkle',
      duration: action.duration || 3000,
    };
  }

  private createHeartsEffect(action: CanvasAction) {
    return {
      type: 'particle',
      particles: 10,
      position: action.position || { x: 0.5, y: 0.8 },
      animation: 'float-up',
      duration: action.duration || 4000,
    };
  }

  private createConfettiEffect(action: CanvasAction) {
    return {
      type: 'particle',
      particles: 50,
      position: action.position || { x: 0.5, y: 0 },
      animation: 'fall',
      duration: action.duration || 5000,
    };
  }

  private createGlowEffect(action: CanvasAction) {
    return {
      type: 'filter',
      effect: 'glow',
      intensity: 0.8,
      color: '#ffffff',
      duration: action.duration || 2000,
    };
  }

  private createRainEffect(action: CanvasAction) {
    return {
      type: 'particle',
      particles: 100,
      position: { x: 0.5, y: 0 },
      animation: 'rain',
      duration: action.duration || 10000,
    };
  }

  // Public methods
  getAllElements(): CanvasElement[] {
    return Array.from(this.elements.values());
  }

  getElement(id: string): CanvasElement | null {
    return this.elements.get(id) || null;
  }

  removeElement(id: string): boolean {
    return this.elements.delete(id);
  }

  updateElement(id: string, updates: Partial<CanvasElement>): boolean {
    const element = this.elements.get(id);
    if (!element) return false;

    Object.assign(element, updates);
    return true;
  }

  getPromptTemplates(): ImagePromptTemplate[] {
    return [...this.promptTemplates];
  }

  addPromptTemplate(template: ImagePromptTemplate): void {
    this.promptTemplates.push(template);
  }
}
